import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Timer, FlaskConical } from "lucide-react";

const Hero = () => {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-24 md:pb-32 overflow-hidden"
    >
      {/* Radial accent glow */}
      <div
        className="pointer-events-none absolute -top-40 right-[-10%] w-[640px] h-[640px] rounded-full opacity-60"
        style={{
          background:
            "radial-gradient(circle, rgba(91,196,209,0.18) 0%, rgba(123,212,222,0.05) 40%, rgba(6,11,25,0) 70%)",
        }}
      />
      <div
        className="pointer-events-none absolute bottom-[-200px] left-[-200px] w-[520px] h-[520px] rounded-full opacity-40"
        style={{
          background:
            "radial-gradient(circle, rgba(123,212,222,0.10) 0%, rgba(6,11,25,0) 70%)",
        }}
      />
      {/* Grid lines */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-8">
        <motion.div
          className="max-w-3xl"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.div
            data-testid="hero-badge"
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-white/15 bg-white/5 text-xs tracking-wide text-slate-200 mb-8"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#5BC4D1] animate-pulse" />
            Distributeur en France de MeMed Key® &amp; MeMed BV®
          </motion.div>

          <motion.h1
            data-testid="hero-title"
            className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-light leading-[1.05] tracking-tight"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.9 }}
          >
            Infection bactérienne
            <br />
            ou virale&nbsp;?
            <br />
            <span className="font-semibold">
              La réponse en{" "}
              <span className="text-[#5BC4D1]">15 minutes</span>.
            </span>
          </motion.h1>

          <motion.p
            data-testid="hero-subtitle"
            className="mt-6 max-w-xl text-base md:text-lg text-slate-300 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            MeMed BV® analyse la réponse immunitaire de l&apos;hôte
            (TRAIL · IP-10 · CRP) pour aider les cliniciens à différencier
            rapidement les infections bactériennes des infections virales
            — au plus près du patient.
          </motion.p>

          <motion.div
            className="mt-10 flex flex-wrap items-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.7 }}
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => window.dispatchEvent(new Event("nx:open-demo"))}
              className="group bg-[#5BC4D1] text-[#060B19] font-semibold px-6 py-3.5 rounded-full hover:bg-[#3DA9B6] transition-colors inline-flex items-center gap-2"
            >
              Demande de renseignements
              <ArrowRight
                size={18}
                className="transition-transform group-hover:translate-x-1"
              />
            </button>
            <a
              data-testid="hero-cta-secondary"
              href="https://www.me-med.com"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-transparent border border-white/20 text-white px-6 py-3.5 rounded-full hover:bg-white/10 transition-colors inline-flex items-center gap-2"
            >
              Découvrir la technologie
            </a>
          </motion.div>

          <motion.div
            className="mt-12 flex flex-wrap gap-x-8 gap-y-4 text-sm text-slate-400"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2" data-testid="hero-trust-1">
              <ShieldCheck size={16} className="text-[#5BC4D1]" />
              Autorisé FDA 510(k)
            </div>
            <div className="inline-flex items-center gap-2" data-testid="hero-trust-2">
              <Timer size={16} className="text-[#5BC4D1]" />
              Résultat en ~15 min
            </div>
            <div className="inline-flex items-center gap-2" data-testid="hero-trust-3">
              <FlaskConical size={16} className="text-[#5BC4D1]" />
              Biologie délocalisée (POC)
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
